import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL_AUTH } from '../config/api';

const STEPS = { PHONE: 0, OTP: 1, NEW_PASSWORD: 2 };

export default function ForgotPasswordPage() {
  const [step, setStep] = useState(STEPS.PHONE);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const parseApiError = (data) => {
    if (data.errors && Array.isArray(data.errors)) {
      return data.errors.map(e => e.message).join(' ');
    }
    return data.message || 'Something went wrong. Please try again.';
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      setError('Please enter your phone number.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL_AUTH}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ phone: phone.trim() }),
      });
      const data = await response.json();

      if (response.ok) {
        setSuccess('A verification code has been sent to your phone.');
        setStep(STEPS.OTP);
      } else {
        setError(parseApiError(data));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp.trim()) {
      setError('Please enter the verification code.');
      return;
    }
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL_AUTH}/auth/verify-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), reset_token: otp.trim() }),
      });
      const data = await response.json();

      if (response.ok) {
        setSuccess('Code verified. Please set your new password.');
        setStep(STEPS.NEW_PASSWORD);
      } else {
        setError(parseApiError(data));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!password || !confirmPassword) {
      setError('Please fill in both password fields.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL_AUTH}/auth/reset-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          phone: phone.trim(),
          reset_token: otp.trim(),
          password,
          confirm_password: confirmPassword,
        }),
      });
      const data = await response.json();

      if (response.ok) {
        setSuccess('Password changed successfully! Redirecting to login...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(parseApiError(data));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const stepTitles = ['Forgot Password', 'Verify Code', 'New Password'];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-900 to-red-700">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-sm">
        <h2 className="text-2xl font-bold mb-2 text-center text-red-800">{stepTitles[step]}</h2>

        <div className="flex justify-center gap-2 mb-6">
          {stepTitles.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i <= step ? 'bg-red-800 w-10' : 'bg-gray-300 w-6'
              }`}
            />
          ))}
        </div>

        {error && <div className="mb-4 text-red-500 text-sm p-2 bg-red-100 rounded">{error}</div>}
        {success && <div className="mb-4 text-green-700 text-sm p-2 bg-green-100 rounded">{success}</div>}

        {step === STEPS.PHONE && (
          <form onSubmit={handleRequestOtp}>
            <p className="text-gray-600 text-sm mb-4">
              Enter the phone number associated with your account and we'll send you a verification code.
            </p>
            <div className="mb-4">
              <label className="block text-gray-700 mb-2" htmlFor="phone">Phone Number</label>
              <input
                id="phone"
                type="tel"
                placeholder="e.g. 1234567890"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-400"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                autoComplete="tel"
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              className="w-full bg-red-800 text-white py-2 rounded hover:bg-red-900 transition disabled:opacity-60"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Verification Code'}
            </button>
          </form>
        )}

        {step === STEPS.OTP && (
          <form onSubmit={handleVerifyOtp}>
            <p className="text-gray-600 text-sm mb-4">
              Enter the 4-digit code sent to <span className="font-medium text-gray-800">{phone}</span>.
            </p>
            <div className="mb-4">
              <label className="block text-gray-700 mb-2" htmlFor="otp">Verification Code</label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                maxLength={4}
                placeholder="1234"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-400 text-center text-2xl tracking-widest"
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                autoComplete="one-time-code"
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              className="w-full bg-red-800 text-white py-2 rounded hover:bg-red-900 transition disabled:opacity-60"
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
            <button
              type="button"
              onClick={() => { setStep(STEPS.PHONE); setError(''); setSuccess(''); setOtp(''); }}
              className="w-full mt-2 text-red-800 text-sm hover:underline"
            >
              Use a different phone number
            </button>
          </form>
        )}

        {step === STEPS.NEW_PASSWORD && (
          <form onSubmit={handleResetPassword}>
            <div className="mb-4">
              <label className="block text-gray-700 mb-2" htmlFor="new-password">New Password</label>
              <input
                id="new-password"
                type="password"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-400"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="new-password"
                disabled={loading}
                minLength={6}
              />
            </div>
            <div className="mb-6">
              <label className="block text-gray-700 mb-2" htmlFor="confirm-password">Confirm Password</label>
              <input
                id="confirm-password"
                type="password"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-red-400"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                disabled={loading}
                minLength={6}
              />
            </div>
            <button
              type="submit"
              className="w-full bg-red-800 text-white py-2 rounded hover:bg-red-900 transition disabled:opacity-60"
              disabled={loading}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}

        <p className="text-center text-sm text-gray-600 mt-6">
          Remember your password? <Link to="/login" className="text-red-800 hover:underline">Back to Login</Link>
        </p>
      </div>
    </div>
  );
}
